import { CardWithHeader } from "@/components/card-with-header";
import { AdminAddRequirementDialog } from "@/components/compliance/admin-add-requirement-dialog";
import { AdminAddSupportingInfoDialog } from "@/components/compliance/admin-add-supporting-info-dialog";
import { AdminCaseDetailsPanel } from "@/components/compliance/admin-case-details-panel";
import { AdminCaseHeader } from "@/components/compliance/admin-case-header";
import { AdminExpandableRequirementRow } from "@/components/compliance/admin-expandable-requirement-row";
import { AdminOptimusUploadsSection } from "@/components/compliance/admin-optimus-uploads-section";
import { AdminDeleteCaseDialog } from "@/components/compliance/admin-delete-case-dialog";
import { AdminUploadToOptimusDialog } from "@/components/compliance/admin-upload-to-optimus-dialog";
import { RefreshIconButton } from "@/components/refresh-icon-button";
import { Spinner } from "@/components/ui/spinner";
import { canAdminRead, canAdminWrite } from "@/lib/auth";
import {
  complianceQueryKeys,
  getAdminCaseDownloadUrlsQueryOptions,
  getAdminCaseQueryOptions,
  getAdminMerchantsQueryOptions,
} from "@/lib/compliance-query-keys";
import { useAdminComplianceCase } from "@/hooks/useAdminComplianceCase";
import { useAdminComplianceCaseMutations } from "@/hooks/useAdminComplianceCaseMutations";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute(
  "/(app)/_admin-layout/admin_/compliance_/cases_/$caseId",
)({
  loader: async ({
    context: { queryClient, accessToken, authorities },
    params: { caseId },
  }) => {
    if (!canAdminRead(authorities)) return;
    await Promise.all([
      queryClient.ensureQueryData(
        getAdminCaseQueryOptions({ accessToken, caseId }),
      ),
      queryClient.ensureQueryData(
        getAdminMerchantsQueryOptions({ accessToken }),
      ),
      queryClient.ensureQueryData(
        getAdminCaseDownloadUrlsQueryOptions({ accessToken, caseId }),
      ),
    ]);
  },
  component: AdminComplianceCaseDetail,
});

function AdminComplianceCaseDetail() {
  const { caseId } = Route.useParams();
  const context = Route.useRouteContext();
  const queryClient = useQueryClient();

  const hasReadAccess = canAdminRead(context.authorities);
  const hasWriteAccess = canAdminWrite(context.authorities);

  const navigate = useNavigate();

  const [addRequirementOpen, setAddRequirementOpen] = useState(false);
  const [addSupportingInfoOpen, setAddSupportingInfoOpen] = useState(false);
  const [uploadToOptimusConfirmOpen, setUploadToOptimusConfirmOpen] =
    useState(false);
  const [deleteCaseOpen, setDeleteCaseOpen] = useState(false);

  const caseQuery = useAdminComplianceCase({
    accessToken: context.accessToken,
    caseId,
  });

  const merchantsQuery = useQuery(
    getAdminMerchantsQueryOptions({ accessToken: context.accessToken }),
  );

  const downloadUrlsQuery = useQuery(
    getAdminCaseDownloadUrlsQueryOptions({
      accessToken: context.accessToken,
      caseId,
    }),
  );

  const merchantName =
    caseQuery.data?.merchantId && merchantsQuery.data
      ? merchantsQuery.data.find(
          (m) => m.merchantId === caseQuery.data?.merchantId,
        )?.companyName
      : undefined;

  const mutations = useAdminComplianceCaseMutations({
    accessToken: context.accessToken,
    caseId,
    caseData: caseQuery.data,
    merchantName,
    onUploadToOptimusSuccess: () => setUploadToOptimusConfirmOpen(false),
    onAddRequirementSuccess: () => setAddRequirementOpen(false),
    onDeleteCaseSuccess: () => {
      setDeleteCaseOpen(false);
      void navigate({ to: "/admin/compliance/cases" });
    },
  });

  if (!hasReadAccess) {
    return (
      <CardWithHeader variant="exchange" label="Admin" title="Case Details">
        <div className="rounded-xl border border-slate-200 bg-slate-50 py-8 text-center text-slate-600">
          You do not have permission to view this case.
        </div>
      </CardWithHeader>
    );
  }

  const data = caseQuery.data;

  const showApproveCase =
    data &&
    hasWriteAccess &&
    data.workflowStatus !== "APPROVED" &&
    (data.workflowStatus === "PENDING_VOLUME_REVIEW" ||
      data.requirements.some((r) => r.status === "SUBMITTED"));

  return (
    <CardWithHeader
      variant="exchange"
      label="Admin"
      title="Case Details"
      headerContent={
        <RefreshIconButton
          label="Refresh admin case details"
          onRefresh={() => caseQuery.refetch()}
        />
      }
    >
      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/admin/compliance/cases"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-volume-text-purple hover:underline"
        >
          <ArrowLeft className="size-4" />
          Back to cases
        </Link>
        {hasWriteAccess && data ? (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => setDeleteCaseOpen(true)}
          >
            <Trash2 className="size-3.5" />
            Delete Case
          </Button>
        ) : null}
      </div>

      {caseQuery.isLoading ? (
        <div className="flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
          <Spinner width={20} height={20} />
          <span>Loading case details...</span>
        </div>
      ) : data ? (
        <div className="space-y-4">
          <AdminCaseHeader
            data={data}
            merchantName={merchantName}
            hasWriteAccess={hasWriteAccess}
            showApproveCase={!!showApproveCase}
            approveCase={mutations.approveCase}
            revokeApproval={mutations.revokeApproval}
            uploadToOptimus={mutations.uploadToOptimus}
            downloadAll={mutations.downloadAll}
            onOpenUploadToOptimus={() => setUploadToOptimusConfirmOpen(true)}
          />

          <AdminOptimusUploadsSection uploads={data.uploadsToOptimus ?? []} />

          <div className="grid gap-4 lg:grid-cols-12">
            <AdminCaseDetailsPanel
              data={data}
              accessToken={context.accessToken}
              hasWriteAccess={hasWriteAccess}
              downloadUrls={downloadUrlsQuery.data}
              onAddSupportingInfo={() => setAddSupportingInfoOpen(true)}
              deleteSupportingInfo={mutations.deleteSupportingInfo}
            />

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white lg:col-span-8">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-volume-text-purple/70">
                  Requirements ({data.requirements.length})
                </h3>
                {hasWriteAccess ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => setAddRequirementOpen(true)}
                  >
                    <Plus className="size-3.5" />
                    Add requirement
                  </Button>
                ) : null}
              </div>
              <div className="p-2 pt-1.5">
                {data.requirements.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-8 text-center text-sm text-slate-500">
                    No requirements on this case.
                  </div>
                ) : (
                  <div className="divide-y divide-volume-text-purple/10">
                    {data.requirements.map((req, idx) => (
                      <div
                        key={req.requirementId}
                        className={idx === 0 ? "pt-0" : ""}
                      >
                        <AdminExpandableRequirementRow
                          requirement={req}
                          workflowStatus={data.workflowStatus}
                          accessToken={context.accessToken}
                          hasWriteAccess={hasWriteAccess}
                          downloadUrls={downloadUrlsQuery.data ?? []}
                          onInvalidate={mutations.invalidateCaseQueries}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {hasWriteAccess ? (
        <>
          <AdminAddRequirementDialog
            open={addRequirementOpen}
            onOpenChange={setAddRequirementOpen}
            accessToken={context.accessToken}
            existingRequirementCodes={
              data?.requirements.map((r) => r.requirementCode) ?? []
            }
            onSubmit={(code) => mutations.addRequirement.mutate(code)}
            isPending={mutations.addRequirement.isPending}
          />
          <AdminAddSupportingInfoDialog
            open={addSupportingInfoOpen}
            onOpenChange={setAddSupportingInfoOpen}
            caseId={caseId}
            accessToken={context.accessToken}
            onSuccess={() => {
              void queryClient.invalidateQueries({
                queryKey: complianceQueryKeys.adminCase(caseId),
              });
              void queryClient.invalidateQueries({
                queryKey: complianceQueryKeys.adminCaseDownloadUrls(caseId),
              });
            }}
          />
          <AdminUploadToOptimusDialog
            open={uploadToOptimusConfirmOpen}
            onOpenChange={setUploadToOptimusConfirmOpen}
            uploads={data?.uploadsToOptimus ?? []}
            onConfirm={() => mutations.uploadToOptimus.mutate()}
            isPending={mutations.uploadToOptimus.isPending}
          />
          <AdminDeleteCaseDialog
            open={deleteCaseOpen}
            onOpenChange={setDeleteCaseOpen}
            customerName={data?.customerName ?? ""}
            onConfirm={() => mutations.deleteCase.mutate()}
            isPending={mutations.deleteCase.isPending}
          />
        </>
      ) : null}
    </CardWithHeader>
  );
}
